import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
  Typography,
  Alert,
  Box,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Divider,
  Button,
  MenuItem,
  Skeleton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { DOCLINK_COLORS } from "@/config/colors";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import { hubMedAdminDataGridSx } from "@/utils/adminTableStyles";
import { formatHubMedNoteDateTime } from "@/utils/hubMedFormatting";
import { getChemistName } from "@/utils/textUtils";
import useAlert from "@/components/feedback/useAlert";
import HubMedOnboardingDrawer from "@/components/admin/hubmed/HubMedOnboardingDrawer";
import {
  normalizeOnboardingListRow,
  normalizeOnboardingDetails,
  hmNum,
} from "@/utils/hubMedApiNormalize";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";

const statusCellWrap = (status) => {
  const s = (status || "").toString().toLowerCase().trim();
  if (s === "active") {
    return <CheckCircleIcon color="success" fontSize="small" titleAccess="Active" />;
  }
  if (s.includes("trial")) {
    return <WarningIcon color="warning" fontSize="small" titleAccess="Trialing" />;
  }
  return (
    <Typography variant="body2" color="text.secondary" textAlign="center" component="span">
      —
    </Typography>
  );
};

const HubMedOnboardingPage = () => {
  const { showAlert, AlertSnackbar } = useAlert();
  const [chemists, setChemists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const data = await phappsRequest(
        API_PATHS.HUBMED_ADMIN_ONBOARDING_CHEMISTS,
        "GET"
      );
      const rawList = data.chemists ?? data.Chemists ?? [];
      setChemists(
        Array.isArray(rawList)
          ? rawList.map(normalizeOnboardingListRow).filter(Boolean)
          : []
      );
    } catch (err) {
      console.error(err);
      setChemists([]);
      setListError(
        getAdminApiErrorMessage(err, "Failed to load onboarding chemists")
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const selectedChemistId = selected?.ChemistID;

  useEffect(() => {
    if (!drawerOpen || !selectedChemistId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setDetailsLoading(true);
        setDetails(null);
        const d = await phappsRequest(
          API_PATHS.HUBMED_ADMIN_ONBOARDING_DETAILS,
          "GET",
          null,
          { chemistId: selectedChemistId }
        );
        if (!cancelled) setDetails(normalizeOnboardingDetails(d));
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          if (err.status === 404) {
            showAlert({
              severity: "error",
              message: "Chemist not found.",
            });
            setDrawerOpen(false);
            setSelected(null);
          } else {
            showAlert({
              severity: "error",
              message: getAdminApiErrorMessage(
                err,
                "Failed to load onboarding details"
              ),
            });
          }
        }
      } finally {
        if (!cancelled) setDetailsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [drawerOpen, selectedChemistId, showAlert]);

  const filteredRows = useMemo(() => {
    let rows = chemists;
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) =>
        [r.ChemistName, r.Phone, r.Email].some((f) =>
          String(f || "")
            .toLowerCase()
            .includes(q)
        )
      );
    }
    if (statusFilter !== "all") {
      rows = rows.filter((r) => {
        const s = (r.StripeSubStatus || "").toString().toLowerCase();
        if (statusFilter === "active") return s === "active";
        if (statusFilter === "trialing") return s.includes("trial");
        return s !== "active" && !s.includes("trial");
      });
    }
    const hasNotes = (r) => hmNum(r.CommentCount, 0) > 0;
    const byChemistName = (a, b) =>
      String(a.ChemistName || "").localeCompare(String(b.ChemistName || ""), undefined, {
        sensitivity: "base",
      });
    rows = [...rows].sort((a, b) => {
      const aN = hasNotes(a);
      const bN = hasNotes(b);
      if (aN && !bN) return -1;
      if (!aN && bN) return 1;
      return byChemistName(a, b);
    });
    return rows.map((r, i) => ({
      id: r.ChemistID ?? r.ChemistUUID ?? `r-${i}`,
      ...r,
    }));
  }, [chemists, search, statusFilter]);

  const columns = [
    {
      field: "ChemistName",
      headerName: "Chemist name",
      flex: 1,
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (p) => getChemistName(p.value),
    },
    {
      field: "Phone",
      headerName: "Phone",
      width: 130,
      headerAlign: "center",
      align: "center",
      sortable: false,
    },
    {
      field: "Email",
      headerName: "Email",
      flex: 1,
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      sortable: false,
    },
    {
      field: "StripeSubStatus",
      headerName: "Quda status",
      width: 110,
      headerAlign: "center",
      align: "center",
      sortable: false,
      cellClassName: "hubmed-onboarding-quda-cell",
      renderCell: (p) => statusCellWrap(p.value),
    },
    {
      field: "CommentCount",
      headerName: "Notes",
      width: 90,
      type: "number",
      headerAlign: "center",
      align: "center",
      sortable: false,
    },
    {
      field: "LatestCommentDateTime",
      headerName: "Latest note",
      width: 170,
      headerAlign: "center",
      align: "center",
      sortable: false,
      valueGetter: (_v, row) =>
        row.LatestCommentDateTime
          ? new Date(row.LatestCommentDateTime).getTime()
          : null,
      renderCell: (p) =>
        p.row.LatestCommentDateTime
          ? formatHubMedNoteDateTime(p.row.LatestCommentDateTime)
          : "—",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (p) => (
        <Button
          size="small"
          variant="contained"
          endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => {
            e.stopPropagation();
            setSelected(p.row);
            setDrawerOpen(true);
          }}
          sx={{
            textTransform: "none",
            backgroundColor: DOCLINK_COLORS.MID_PURPLE,
            minWidth: 72,
          }}
        >
          Open
        </Button>
      ),
    },
  ];

  return (
    <div className="h-full min-h-0 flex flex-col bg-white overflow-x-hidden">
      <div className="flex-1 min-h-0 flex flex-col p-2 sm:p-4 space-y-3 w-full">
        {AlertSnackbar}

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{ color: DOCLINK_COLORS.DARK_PURPLE }}
            >
              Prospective Chemists
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Track HubMed outreach, notes, and visibility.
            </Typography>
          </Box>
          <Button
            component={NavLink}
            to="/hubmed-admin/overview"
            variant="outlined"
            size="small"
            sx={{
              textTransform: "none",
              borderColor: DOCLINK_COLORS.DARK_PURPLE,
              color: DOCLINK_COLORS.DARK_PURPLE,
              fontWeight: 600,
            }}
          >
            Back to overview
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 900 }}>
          <strong>Quda status</strong>
          {
            " refers to the chemist's subscription to Pharmacy Halo's Quda — if they are subscribed to Quda they will be more familiar with the platform and potentially easier to chat to about DocLink."
          }
        </Typography>

        {listError ? <Alert severity="warning">{listError}</Alert> : null}

        <Card
          variant="outlined"
          sx={{
            borderColor: DOCLINK_COLORS.DARK_PURPLE,
            borderRadius: 2,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CardContent sx={{ p: 0, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                placeholder="Search name, phone, or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 260, flex: "1 1 220px" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                select
                size="small"
                label="Quda status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="trialing">Trialing</MenuItem>
                <MenuItem value="other">Other / none</MenuItem>
              </TextField>
            </Box>
            <Divider />
            {loading ? (
              <Box sx={{ flex: 1, minHeight: 240, p: 2 }}>
                <Skeleton variant="rounded" height="100%" sx={{ minHeight: 360 }} />
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  width: "100%",
                  px: 1,
                  pb: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <DataGrid
                  rows={filteredRows}
                  columns={columns}
                  loading={loading}
                  pageSizeOptions={[25, 50, 100]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 100 } },
                  }}
                  onRowClick={(p) => {
                    setSelected(p.row);
                    setDrawerOpen(true);
                  }}
                  disableRowSelectionOnClick
                  sx={{
                    ...hubMedAdminDataGridSx,
                    height: "100%",
                    minHeight: 0,
                    "& .MuiDataGrid-row": { cursor: "pointer" },
                    "& .MuiDataGrid-cell.hubmed-onboarding-quda-cell": {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </div>

      <HubMedOnboardingDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelected(null);
          setDetails(null);
          loadList();
        }}
        chemistId={selected?.ChemistID}
        details={details}
        detailsLoading={detailsLoading}
        showAlert={showAlert}
        onReplaceDetails={(payload) => setDetails(payload)}
        onAfterHide={loadList}
      />
    </div>
  );
};

export default HubMedOnboardingPage;
